import { Component, Input, OnInit } from "@angular/core";
import { FormGroup } from "@angular/forms";
import { DomSanitizer } from "@angular/platform-browser";
import { select, Store } from "@ngrx/store";
import { LangChangeEvent, TranslateService } from "@ngx-translate/core";
import { ToastrService } from "ngx-toastr";
import { Observable, takeUntil } from "rxjs";
import { BaseComponent } from "src/app/base.component";
import { Constants } from "src/app/shared/data/constants";
import { FranchiseProfileInterface } from "src/app/shared/models/franchise/franchise-profile.interface";
import { IberisActivities } from "src/app/shared/models/iberis/activities";
import { AppStateInterface } from "src/app/store/appState.interface";
import { invokeAllCategoryNames } from "src/app/store/category/category.actions";
import { allCategoryNamesSelector } from "src/app/store/category/category.selectors";
import { invokeUploadFile } from "src/app/store/file/file.actions";
import { uploadFileSelector } from "src/app/store/file/file.selectors";
import { resultFranchiseInfoSelector } from "src/app/store/franchise/franchise.selectors";
import { invokeSubCategoriesByCategory } from "src/app/store/subCategory/subCategory.actions";
import { subCategoriesByCategoryNameSelector } from "src/app/store/subCategory/subCategory.selectors";
import { invokeAllTagNames } from "src/app/store/tag/tag.actions";
import { allTagNamesSelector } from "src/app/store/tag/tag.selectors";
import { setCheckUserExistsByCompanyName } from "src/app/store/user/user.actions";
import { checkUserExistByCompanyNameSelector } from "src/app/store/user/user.selectors";
import { environment } from "src/environments/environment";

@Component({
  selector: "app-company-information",
  templateUrl: "./company-information.component.html",
})
export class CompanyInformationComponent
  extends BaseComponent
  implements OnInit
{
  @Input("form") form: FormGroup;
  @Input("option") option: string | null;
  @Input() create: boolean;
  @Input() isEmailCheck : boolean = false
  @Input() isFranchised: boolean = false;
  @Input() file: string | null = null;
  
  companyActivity: IberisActivities[];

  //category
  allCategoryNames$: Observable<string[] | null>;
  getCategoryNames: string[] | null;

  //subCategoryByCategory
  subCategoriesByCategory$: Observable<string[] | null>;
  getSubCategoriesByCategory: string[] | null;

  //tag
  allTagNames$: Observable<string[] | null>;
  getTagNames: string[];

  franchiseInfo$: Observable<FranchiseProfileInterface | null>;

  franchise: boolean;
  showError : boolean
  isUploaded: boolean;
  files: File[] = [];
  uploadFile$: Observable<string | null>;
  isShown: boolean
  isExist$: Observable<boolean | null>;
  uploadedFile: string | null = null;

  constructor(
    private store: Store<AppStateInterface>,
    private translate: TranslateService,
    private toastr: ToastrService,
    private sanitizer: DomSanitizer
  ) {
    super(store);
    this.isExist$ = this.store
    .pipe(select(checkUserExistByCompanyNameSelector))
    .pipe(takeUntil(this.ngDestroyed$));
    this.uploadFile$ = this.store
    .pipe(select(uploadFileSelector))
    .pipe(takeUntil(this.ngDestroyed$));
    this.allCategoryNames$ = this.store
      .pipe(select(allCategoryNamesSelector))
      .pipe(takeUntil(this.ngDestroyed$));
    this.subCategoriesByCategory$ = this.store
      .pipe(select(subCategoriesByCategoryNameSelector))
      .pipe(takeUntil(this.ngDestroyed$));
    this.allTagNames$ = this.store
      .pipe(select(allTagNamesSelector))
      .pipe(takeUntil(this.ngDestroyed$));
    this.franchiseInfo$ = this.store
      .pipe(select(resultFranchiseInfoSelector))
      .pipe(takeUntil(this.ngDestroyed$));
  }

  ngOnInit(): void {
    this.uploadedFile = this.file;
    this.isExist$.subscribe((data) => {
      
      if (data == true) {
        this.toastr.error(
          this.translate.instant("response.error.companyName-exist"),
          this.translate.instant("response.errorTitle")
        );
        this.store.dispatch(setCheckUserExistsByCompanyName({isExistByCompanyName : null}))
      }
    });

    this.uploadFile$.subscribe((file) => {
      if (file) {
        this.uploadedFile = file;
        this.form.patchValue({ logo: this.uploadedFile });
      }
    })

    if (this.translate.currentLang == "fr")
      this.companyActivity = Constants.IBERIS_ACTIVITIES_FR;
    else this.companyActivity = Constants.IBERIS_ACTIVITIES_EN;

    this.translate.onLangChange.subscribe(
      (langChangeEvent: LangChangeEvent) => {
        if (langChangeEvent.lang == "fr")
          this.companyActivity = Constants.IBERIS_ACTIVITIES_FR;
        else this.companyActivity = Constants.IBERIS_ACTIVITIES_EN;
      }
    );

    if (this.option?.endsWith("franchise")) {
      this.franchise = false;
    }
    this.allCategoryNames$.subscribe((result: string[] | null) => {
      if (result == null) {
        this.store.dispatch(invokeAllCategoryNames());
      }
    });
    if(this.form.get("category")?.value){
      this.store.dispatch(
        invokeSubCategoriesByCategory({
          categoryName: this.form.get("category")?.value,
        })
      );
    }
    this.subCategoriesByCategory$.subscribe((result: string[] | null) => {
      if (result !== null) {
        this.getSubCategoriesByCategory = result;
      }
    });

    this.allTagNames$.subscribe((result: string[] | null) => {
      if (result == null) {
        this.store.dispatch(invokeAllTagNames());
      }
    });

    this.franchiseInfo$.subscribe((result: FranchiseProfileInterface | null) => {
      if (this.create && result !== null) {
        if (result.category){
          this.store.dispatch(
            invokeSubCategoriesByCategory({
              categoryName: result.category,
            })
          );
          this.form.patchValue({ category: result.category });
        }
        if (result.subCategories?.length) {
          this.form.patchValue({ subCategories: [...result.subCategories] });
        }
        if (result.tags?.length) {
          this.form.patchValue({ tags: [...result.tags] });
        }
        if (result.companyActivity) {
          this.form.patchValue({ companyActivity: result.companyActivity })
        }
        if (result.logo){
          this.uploadedFile = result.logo;
          this.form.patchValue({ logo: result.logo });
        }
      }
    });
  }

  onFileSelected(event: any) {
    if (event.target.files[0].size > 4194304) this.showError = true;
    this.files = event.target.files;
    this.isShown = true;
    const formData = new FormData();
    formData.append("files", this.files[0], this.files[0].name);
    this.store.dispatch(invokeUploadFile({ files: formData }));
    // this.showlogo();
  }

  showlogo() {
    const logo = this.uploadedFile ?? this.form.get("logo")?.value;
    if ( logo ) {
      return this.sanitizer.bypassSecurityTrustResourceUrl(
        environment.CDN_RELEAD + logo
      );
    } else return null;
  }

  onChangeCategory() {
    this.form.get("subCategories")?.reset([]);
    this.store.dispatch(
      invokeSubCategoriesByCategory({
        categoryName: this.form.get("category")?.value,
      })
    );
  }

}
